/**
 * Text field for the auth and entry surfaces, composed from the base/input
 * primitives so a react-hook-form field can feed it via `base/form`'s
 * `FormField` render prop.
 *
 * The auth chrome constants (`AUTH_INPUT_CLASS`, `entryFieldClass`, the
 * `getAuthPageChrome()` roles) style the input ELEMENT: they were authored
 * against a plain bordered `<input>`, and the desktop entry canvas variants are
 * deliberately single-theme, so re-expressing them on `InputBase`'s group
 * wrapper would change what renders. Instead the wrapper is made inert and the
 * box chrome stays on the input, where those constants expect it.
 */

import { HintText } from '@/components/base/input/hint-text';
import { InputBase, TextField } from '@/components/base/input/input';
import { Label } from '@/components/base/input/label';
import { cx } from '@/lib/utils/cx';

import type { ReactNode, Ref } from 'react';
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components';

/**
 * Silences every box-model class `InputBase` paints on its group wrapper —
 * including the focus-within and group-invalid ring variants — so the only
 * visible box is the input's own border. `group-disabled:bg-transparent`
 * counters the wrapper's disabled fill for the same reason.
 */
const INERT_WRAPPER_CLASS =
  'rounded-none bg-transparent shadow-none ring-0 group-invalid:ring-0 group-disabled:bg-transparent';

/**
 * Restores what the retired `ui/input` base supplied underneath the chrome
 * constants: a real border (they set only the colour), and the dimmed
 * disabled state.
 */
const INPUT_BOX_CLASS =
  'rounded-md border border-solid disabled:cursor-not-allowed disabled:opacity-50';

interface AuthTextFieldProps extends AriaTextFieldProps {
  readonly label: string;
  readonly labelClassName?: string;
  /** Box chrome for the input element (`AUTH_INPUT_CLASS`, `entryFieldClass`, …). */
  readonly inputClassName?: string;
  readonly placeholder?: string;
  /** Error copy under the field, rendered in the error-message slot. */
  readonly hint?: string;
  /** Overlay control inside the input box (the password reveal toggle). */
  readonly trailing?: ReactNode;
  /** Forwarded to the input element, where react-hook-form focuses errors. */
  readonly ref?: Ref<HTMLInputElement>;
}

export function AuthTextField({
  label,
  labelClassName,
  inputClassName,
  placeholder,
  hint,
  trailing,
  ref,
  ...textFieldProps
}: AuthTextFieldProps) {
  return (
    <TextField {...textFieldProps}>
      <Label className={labelClassName}>{label}</Label>
      <div className="relative w-full">
        <InputBase
          ref={ref}
          inputClassName={cx(INPUT_BOX_CLASS, inputClassName)}
          placeholder={placeholder}
          wrapperClassName={INERT_WRAPPER_CLASS}
        />
        {trailing}
      </div>
      {hint ? <HintText isInvalid>{hint}</HintText> : null}
    </TextField>
  );
}
